import * as React from 'react';
import type { RetailerSectionId } from './sections';
import type { RetailerWithFormats } from './types';

/**
 * UI workspace context for Retailer Management.
 * This is a navigation/filter seam, not an authorisation boundary.
 * Server-enforced retailer access remains a later phase.
 */
export interface RetailerWorkspaceNavigation {
  mode: 'platform' | 'retailer';
  retailers: RetailerWithFormats[];
  activeRetailer: RetailerWithFormats | null;
  activeRetailerId: number | null;
  activeSection: RetailerSectionId;
  showArchivedRetailers: boolean;
  setShowArchivedRetailers: (show: boolean) => void;
  /** Raw navigation — prefer the guarded helpers below from UI controls. */
  navigateToRetailer: (retailerId: number, section?: RetailerSectionId) => void;
  navigateToSection: (section: RetailerSectionId) => void;
  navigateToDirectory: () => void;
}

export interface RetailerWorkspaceValue extends RetailerWorkspaceNavigation {
  hasUnsavedChanges: boolean;
  setUnsavedChanges: (sourceId: string, isDirty: boolean) => void;
  selectRetailer: (retailerId: number, section?: RetailerSectionId) => void;
  selectSection: (section: RetailerSectionId) => void;
  clearRetailer: () => void;
  pendingNavigation: (() => void) | null;
  confirmPendingNavigation: () => void;
  cancelPendingNavigation: () => void;
}

const RetailerWorkspaceContext = React.createContext<RetailerWorkspaceValue | undefined>(undefined);

export function RetailerWorkspaceProvider({
  value,
  children,
}: {
  value: RetailerWorkspaceNavigation;
  children: React.ReactNode;
}) {
  const [dirtySources, setDirtySources] = React.useState<Set<string>>(() => new Set());
  const [pendingNavigation, setPendingNavigation] = React.useState<(() => void) | null>(null);

  const hasUnsavedChanges = dirtySources.size > 0;

  const setUnsavedChanges = React.useCallback((sourceId: string, isDirty: boolean) => {
    setDirtySources((prev) => {
      const alreadyDirty = prev.has(sourceId);
      if (isDirty && alreadyDirty) return prev;
      if (!isDirty && !alreadyDirty) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(sourceId);
      else next.delete(sourceId);
      return next;
    });
  }, []);

  const clearUnsavedChanges = React.useCallback(() => {
    setDirtySources(new Set());
  }, []);

  const requestNavigate = React.useCallback(
    (navigate: () => void) => {
      if (hasUnsavedChanges) {
        setPendingNavigation(() => navigate);
        return;
      }
      navigate();
    },
    [hasUnsavedChanges],
  );

  const selectRetailer = React.useCallback(
    (retailerId: number, section: RetailerSectionId = value.activeSection) => {
      if (retailerId === value.activeRetailerId && section === value.activeSection) return;
      requestNavigate(() => value.navigateToRetailer(retailerId, section));
    },
    [requestNavigate, value],
  );

  const selectSection = React.useCallback(
    (section: RetailerSectionId) => {
      if (section === value.activeSection) return;
      requestNavigate(() => value.navigateToSection(section));
    },
    [requestNavigate, value],
  );

  const clearRetailer = React.useCallback(() => {
    requestNavigate(() => value.navigateToDirectory());
  }, [requestNavigate, value]);

  const confirmPendingNavigation = React.useCallback(() => {
    const navigate = pendingNavigation;
    setPendingNavigation(null);
    clearUnsavedChanges();
    navigate?.();
  }, [clearUnsavedChanges, pendingNavigation]);

  const cancelPendingNavigation = React.useCallback(() => {
    setPendingNavigation(null);
  }, []);

  React.useEffect(() => {
    if (!hasUnsavedChanges) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  const contextValue = React.useMemo<RetailerWorkspaceValue>(
    () => ({
      ...value,
      hasUnsavedChanges,
      setUnsavedChanges,
      selectRetailer,
      selectSection,
      clearRetailer,
      pendingNavigation,
      confirmPendingNavigation,
      cancelPendingNavigation,
    }),
    [
      value,
      hasUnsavedChanges,
      setUnsavedChanges,
      selectRetailer,
      selectSection,
      clearRetailer,
      pendingNavigation,
      confirmPendingNavigation,
      cancelPendingNavigation,
    ],
  );

  return (
    <RetailerWorkspaceContext.Provider value={contextValue}>
      {children}
    </RetailerWorkspaceContext.Provider>
  );
}

export function useRetailerWorkspace(): RetailerWorkspaceValue {
  const ctx = React.useContext(RetailerWorkspaceContext);
  if (!ctx) {
    throw new Error('useRetailerWorkspace must be used within RetailerWorkspaceProvider');
  }
  return ctx;
}

/** Optional hook for shared section components that may render outside the workspace. */
export function useOptionalRetailerWorkspace(): RetailerWorkspaceValue | null {
  return React.useContext(RetailerWorkspaceContext) ?? null;
}
